import React, { useEffect, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import { MaterialSymbol } from 'spa/member/components/MaterialSymbol'
import { StyledTodoListDropdownProps, TodoListItemProps } from './types'
import styles from './TodoListDropdown.module.scss'
import checkCircle from 'images/spa/header/check_circle.svg'
import { profile_sheet_information_disclosure_path, recommended_job_offers_path, job_offers_path, profile_sheet_resume_files_path, profile_sheet_resume_path } from 'routes'

// 登録後にやることリストの1項目（チェック状態 + 関連リンク）
const TodoListItem: React.FC<TodoListItemProps> = ({ label, completed, recommended, links }) => (
  <section className="flex flex-col gap-[2px] items-start w-full">
    <div className="flex gap-XS items-center w-full py-[2px]">
      <div className={`flex items-center justify-center shrink-0 size-[24px] relative rounded-S ${completed ? `bg-tech-apple-green-10 ${styles.completed}` : 'bg-member-gray-20'}`} />
      <div className={`text-M font-bold leading-[1.7] ${completed ? 'text-tech-black-60' : 'text-tech-black-100'}`}>{label}</div>
      {recommended && (
        <span className="border border-tech-silver-100! rounded-S px-XS text-XS font-bold text-tech-silver-100">推奨</span>
      )}
    </div>
    <div className="flex flex-col items-start pl-2XL w-full">
      {links.map(link => (
        <a key={link.href} href={link.href} className="flex items-center gap-XS py-2XS w-full font-normal text-S leading-[1.7] !text-tech-black-100 hover:opacity-70">
          <span className="grow leading-[1.7] underline underline-offset-2">{link.label}</span>
          <MaterialSymbol name="arrow_forward_ios" className="text-tech-silver-70" size="12px" />
        </a>
      ))}
    </div>
  </section>
)

// ログイン後ヘッダーの人型アイコン隣に表示する「登録後にやることリスト」
export const StyledTodoListDropdown: React.FC<StyledTodoListDropdownProps> = ({ todoList, onOpen, forceClosed }) => {
  const [isTodoListOpen, setIsTodoListOpen] = useState<boolean>(false)
  const todoListRef = useRef<HTMLDivElement>(null)
  useClickAway(todoListRef, () => setIsTodoListOpen(false))

  const onClickButton = () => {
    if (!isTodoListOpen) {
      onOpen()
    }

    setIsTodoListOpen(prev => !prev)
  }

  useEffect(() => {
    if (forceClosed) {
      setIsTodoListOpen(false)
    }
  }, [forceClosed])

  return (
    <div ref={todoListRef} className="relative flex items-center h-full">
      {/* NOTE: レガシー画面ではTailwindのPreflight（リセット）を読み込まないため、
          ブラウザ標準のbutton styleを打ち消すクラスを明示的に指定する */}
      <button
        type="button"
        className="relative flex items-center justify-center size-[40px] p-0 appearance-none bg-transparent border-0 cursor-pointer hover:opacity-70"
        aria-label="登録後にやることリスト"
        aria-expanded={isTodoListOpen}
        aria-controls="header-todo-list-dropdown"
        onClick={onClickButton}
      >
        <img src={checkCircle} alt="登録後にやることリスト" width={20} height={20} />
        {todoList.remainingCount > 0 && (
          <span className="absolute top-2XS right-2XS flex items-center justify-center size-[14px] rounded-FULL bg-tech-orange-80 text-white text-[10px] font-bold leading-none">
            {todoList.remainingCount}
          </span>
        )}
      </button>
      {/* NOTE: ドロップダウンのz-indexはフラッシュメッセージより手前に出すため、
          マイページメニュー（Header.module.scss の .dropdown）と同じ値を指定する */}
      {isTodoListOpen &&
        <div id="header-todo-list-dropdown" className="absolute right-0 top-full mt-2XS w-[256px] z-[999] flex flex-col gap-M bg-white shadow-[0_4px_8px_rgba(34,34,34,0.2)] p-M">
          <div className="flex flex-col items-start w-full">
            <div className="text-S font-bold text-tech-black-100 w-full leading-[1.7]">登録後にやること</div>
            <div className="text-XS font-normal text-tech-black-60 w-full leading-[1.7]">稼働を開始するために以下の対応を行いましょう。</div>
          </div>
          <div className="flex flex-col gap-M items-start w-full">
            <TodoListItem
              label="希望条件を登録"
              completed={todoList.workPreferenceCompleted}
              links={[{ label: '編集する', href: profile_sheet_information_disclosure_path() }]}
            />
            <TodoListItem
              label="案件に応募"
              completed={todoList.jobOfferApplicationCompleted}
              recommended={todoList.jobOfferApplicationRecommended}
              links={[
                { label: 'おすすめ案件', href: recommended_job_offers_path() },
                { label: '案件検索', href: job_offers_path() },
              ]}
            />
            <TodoListItem
              label="職務経歴書を提出"
              completed={todoList.resumeCompleted}
              links={[
                { label: 'アップロード', href: profile_sheet_resume_files_path() },
                { label: '経歴を入力', href: profile_sheet_resume_path() },
              ]}
            />
          </div>
        </div>
      }
    </div>
  )
}
